import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, Download, Loader2, Plus, Search, Upload, X } from 'lucide-react';
import { imagePresetsApi } from '../api';
import type { AdminCatalog, AdminPreset, PresetFields } from '../api/imagePresets';
import { presetError } from '../api/imagePresets';
import { useAuth } from '../contexts/AuthContext';
import { useImagePresetCatalog, useReorderPresets, useUpdatePreset } from '../hooks';
import { getErrorDetail } from '../utils/error';
import { AspectShape, Toggle } from '../components/imagePresets/common';
import { serverEnv, toFields } from '../components/imagePresets/format';
import PresetEditor from '../components/imagePresets/PresetEditor';
import VersionHistory from '../components/imagePresets/VersionHistory';
import TrialGeneration from '../components/imagePresets/TrialGeneration';
import CategoriesPanel from '../components/imagePresets/CategoriesPanel';
import ReferenceImagesPanel from '../components/imagePresets/ReferenceImagesPanel';
import ImportDialog from '../components/imagePresets/ImportDialog';

type Tab = 'presets' | 'categories' | 'references';

const blankPreset = (catalog: AdminCatalog): PresetFields => {
  // 모델·품질은 가장 최근에 고친 프리셋을 따른다 — 대개 지금 쓰는 조합이다.
  const recent = [...catalog.presets].sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
  return {
    id: '',
    name: '',
    category: catalog.categories[0]?.id ?? '',
    description: '',
    prompt: '',
    negative_prompt: null,
    variables: [],
    allow_text_in_image: false,
    aspect: 'source',
    reference_images: [],
    provider: recent?.provider ?? 'openai',
    model: recent?.model ?? '',
    quality: recent?.quality ?? 'low',
    enabled: false,
    order: 0,
  };
};

const downloadJson = (data: unknown, filename: string) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ImagePresets: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'super_admin';
  const env = serverEnv();
  const queryClient = useQueryClient();
  const catalog = useImagePresetCatalog();
  const update = useUpdatePreset();
  const reorder = useReorderPresets();

  const [tab, setTab] = useState<Tab>('presets');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 새로 만들기·복제의 시작 내용. 있으면 편집 칸이 "새 프리셋" 이 된다.
  const [newDraft, setNewDraft] = useState<{ fields: PresetFields; seq: number } | null>(null);
  const [historyFor, setHistoryFor] = useState<AdminPreset | null>(null);
  const [trialDraft, setTrialDraft] = useState<PresetFields | null>(null);
  const [importing, setImporting] = useState(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [onlyEnabled, setOnlyEnabled] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const data = catalog.data;
  const selected = data?.presets.find((p) => p.id === selectedId) ?? null;

  const groups = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.categories.map((category) => ({
      category,
      all: data.presets.filter((p) => p.category === category.id),
      shown: data.presets.filter(
        (p) =>
          p.category === category.id &&
          (!onlyEnabled || p.enabled) &&
          (!needle || p.id.includes(needle) || p.name.toLowerCase().includes(needle)),
      ),
    }));
  }, [data, search, onlyEnabled]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['image-presets'] });

  const toggleEnabled = async (preset: AdminPreset, enabled: boolean) => {
    setNotice(null);
    try {
      await update.mutateAsync({ id: preset.id, version: preset.version, changes: { enabled } });
    } catch (e) {
      const body = presetError(e);
      const detail = body?.errors?.map((err) => `${err.loc.join('.')}: ${err.msg}`).join(' · ');
      setNotice(`${preset.id} — ${detail ?? getErrorDetail(e, t('imagePresets.saveFailed'))}`);
      if (body?.error_code === 'IMAGE_PRESET_VERSION_CONFLICT') refresh();
    }
  };

  const move = (ids: string[], category: string, index: number, delta: number) => {
    const next = [...ids];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    reorder.mutate(
      { category, ids: next },
      { onError: (e) => setNotice(getErrorDetail(e, t('imagePresets.saveFailed'))) },
    );
  };

  const exportChecked = async () => {
    setExporting(true);
    try {
      const bundle = await imagePresetsApi.exportBundle(checked);
      const day = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
      downloadJson(bundle, `image-presets-${env}-${day}.json`);
    } catch (e) {
      setNotice(getErrorDetail(e, t('imagePresets.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  const startNew = (fields: PresetFields) => {
    setSelectedId(null);
    setNewDraft((prev) => ({ fields, seq: (prev?.seq ?? 0) + 1 }));
  };

  if (catalog.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card p-6 flex gap-3 text-sm text-slate-700">
        <AlertTriangle className="text-amber-500 shrink-0" />
        {getErrorDetail(catalog.error, t('imagePresets.loadFailed'))}
      </div>
    );
  }

  const enabledCount = data.presets.filter((p) => p.enabled).length;
  const editing = newDraft && !selected ? null : selected;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2.5">
            {t('imagePresets.title')}
            <span
              className={`badge ${env === 'prod' ? 'bg-rose-100 text-rose-800 ring-1 ring-rose-200' : 'bg-slate-100 text-slate-700'}`}
              title={t('imagePresets.envHint')}
            >
              {env.toUpperCase()}
            </span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('imagePresets.subtitle', { env, enabled: enabledCount, total: data.presets.length })}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              onClick={() => setImporting(true)}
            >
              <Upload size={15} /> {t('imagePresets.import')}
            </button>
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              onClick={() => startNew(blankPreset(data))}
            >
              <Plus size={15} /> {t('imagePresets.newPreset')}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {(
          [
            ['presets', t('imagePresets.tabPresets'), data.presets.length],
            ['categories', t('imagePresets.tabCategories'), data.categories.length],
            ['references', t('imagePresets.tabReferences'), data.reference_images.length],
          ] as [Tab, string, number][]
        ).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {label} <span className="text-xs text-slate-400 tabular-nums">{count}</span>
          </button>
        ))}
      </div>

      {notice && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          <span className="flex-1">{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label={t('imagePresets.close')}>
            <X size={16} />
          </button>
        </div>
      )}

      {tab === 'categories' && <CategoriesPanel catalog={data} canEdit={canEdit} />}
      {tab === 'references' && <ReferenceImagesPanel catalog={data} canEdit={canEdit} />}

      {tab === 'presets' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] items-start">
          <div className="card">
            <div className="p-3 border-b border-slate-200 space-y-2">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input pl-9"
                  placeholder={t('imagePresets.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label={t('imagePresets.search')}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <label className="inline-flex items-center gap-1.5 text-slate-600">
                  <input type="checkbox" checked={onlyEnabled} onChange={(e) => setOnlyEnabled(e.target.checked)} />
                  {t('imagePresets.onlyEnabled')}
                </label>
                {canEdit && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-blue-700 hover:underline disabled:text-slate-400 disabled:no-underline"
                    disabled={checked.length === 0 || exporting}
                    onClick={exportChecked}
                    title={t('imagePresets.exportHint')}
                  >
                    {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    {t('imagePresets.exportChecked', { count: checked.length })}
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[calc(100vh-17rem)] overflow-y-auto">
              {groups.map(({ category, all, shown }) =>
                shown.length === 0 ? null : (
                  <section key={category.id}>
                    <h3 className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur border-y border-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 flex justify-between">
                      <span>{category.name}</span>
                      <span className="font-normal text-slate-400 tabular-nums">
                        {all.filter((p) => p.enabled).length}/{all.length}
                      </span>
                    </h3>
                    <ul>
                      {shown.map((preset) => {
                        const ids = all.map((p) => p.id);
                        const index = ids.indexOf(preset.id);
                        const active = preset.id === selected?.id;
                        return (
                          <li
                            key={preset.id}
                            className={`group flex items-center gap-2.5 px-3 py-2 border-b border-slate-100 cursor-pointer ${
                              active ? 'bg-blue-50' : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setNewDraft(null);
                              setSelectedId(preset.id);
                            }}
                          >
                            {canEdit && (
                              <input
                                type="checkbox"
                                checked={checked.includes(preset.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() =>
                                  setChecked((prev) =>
                                    prev.includes(preset.id)
                                      ? prev.filter((x) => x !== preset.id)
                                      : [...prev, preset.id],
                                  )
                                }
                                aria-label={t('imagePresets.selectForExport', { id: preset.id })}
                              />
                            )}
                            <Toggle
                              checked={preset.enabled}
                              onChange={(v) => toggleEnabled(preset, v)}
                              disabled={!canEdit || update.isPending}
                              label={t('imagePresets.toggleLabel', { name: preset.name })}
                            />
                            <div className="min-w-0 flex-1">
                              <p
                                className={`text-sm truncate ${preset.enabled ? 'text-slate-800 font-medium' : 'text-slate-500'}`}
                              >
                                {preset.name}
                              </p>
                              <p className="text-[11px] text-slate-400 truncate">
                                <span className="font-mono">{preset.id}</span> · {preset.quality ?? '—'} · v
                                {preset.version}
                              </p>
                            </div>
                            <span
                              className="flex items-center gap-1 text-[11px] text-slate-500 w-12 justify-end"
                              title={preset.aspect}
                            >
                              <AspectShape aspect={preset.aspect} size={14} />
                              {preset.aspect === 'source' ? '' : preset.aspect}
                            </span>
                            {canEdit && !search && !onlyEnabled && (
                              <span className="flex flex-col opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                                <button
                                  type="button"
                                  disabled={index === 0 || reorder.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    move(ids, category.id, index, -1);
                                  }}
                                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20"
                                  aria-label={t('imagePresets.moveUp')}
                                >
                                  <ArrowUp size={13} />
                                </button>
                                <button
                                  type="button"
                                  disabled={index === ids.length - 1 || reorder.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    move(ids, category.id, index, 1);
                                  }}
                                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20"
                                  aria-label={t('imagePresets.moveDown')}
                                >
                                  <ArrowDown size={13} />
                                </button>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ),
              )}
            </div>
          </div>

          {editing || newDraft ? (
            <PresetEditor
              key={editing ? `${editing.id}@${editing.version}` : `new-${newDraft?.seq}`}
              preset={editing}
              initial={editing ? toFields(editing) : (newDraft as { fields: PresetFields }).fields}
              catalog={data}
              canEdit={canEdit}
              onSaved={(saved) => {
                setNewDraft(null);
                setSelectedId(saved.id);
                refresh();
              }}
              onDeleted={() => {
                setSelectedId(null);
                setChecked((prev) => prev.filter((id) => id !== editing?.id));
              }}
              onDuplicate={startNew}
              onOpenHistory={() => editing && setHistoryFor(editing)}
              onOpenTrial={setTrialDraft}
            />
          ) : (
            <div className="card p-10 text-center text-sm text-slate-500">{t('imagePresets.pickOne')}</div>
          )}
        </div>
      )}

      {historyFor && (
        <VersionHistory
          preset={data.presets.find((p) => p.id === historyFor.id) ?? historyFor}
          canEdit={canEdit}
          onClose={() => setHistoryFor(null)}
          onRolledBack={() => setHistoryFor(null)}
        />
      )}
      {trialDraft && <TrialGeneration draft={trialDraft} onClose={() => setTrialDraft(null)} />}
      {importing && <ImportDialog onClose={() => setImporting(false)} onImported={refresh} />}
    </div>
  );
};

export default ImagePresets;
